import React from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Accordion,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFaceSmile,
  faChartBar,
  faQrcode,
  faUsers,
  faBolt,
  faStar,
  faImages,
  faArrowRight,
  faCheckCircle,
  faUpload,
  faHeart,
  faShareAlt,
  faShareNodes,
  faPalette,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import HeaderLanding from "../components/HeaderLanding";
import SlideIn from "../components/SlideIn";

// ─── Hero ────────────────────────────────────────────────────────────────────
function HeroSection() {
  const { t } = useTranslation();
  return (
    <section
      className="py-5 text-white text-center"
      style={{
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        padding: "100px 20px 90px",
      }}
    >
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span
            className="badge rounded-pill mb-4 d-inline-block"
            style={{
              backgroundColor: "#e63946",
              fontSize: "0.8rem",
              letterSpacing: "0.08em",
            }}
          >
            {t("photographer_landing.hero.badge")}
          </span>
          <h1
            className="display-4 fw-bold mb-4"
            style={{ maxWidth: 760, margin: "0 auto 1.5rem" }}
          >
            {t("photographer_landing.hero.title")}
          </h1>
          <p
            className="lead mb-5"
            style={{
              maxWidth: 600,
              margin: "0 auto 2rem",
              fontSize: "1.2rem",
              opacity: 0.85,
            }}
          >
            {t("photographer_landing.hero.subtitle")}
          </p>
          <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
            <Button as={Link} to="/signup" size="lg" className="landing-page-btn px-5">
              {t("photographer_landing.hero.cta")}
            </Button>
            <Button
              as={Link}
              to="/plans"
              size="lg"
              variant="outline-light"
              className="px-5"
            >
              {t("photographer_landing.hero.secondary_cta")}
            </Button>
          </div>
          <p className="mt-3 small" style={{ opacity: 0.5 }}>
            {t("photographer_landing.hero.no_card")}
          </p>
          {/* <div
            className="mt-5 rounded-4 overflow-hidden border"
            style={{ borderColor: "rgba(255,255,255,0.15)" }}
          >
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ background: "rgba(255,255,255,0.06)", height: 280 }}
            >
              <p className="text-white-50 small mb-0">Demo preview coming soon</p>
            </div>
          </div> */}
        </motion.div>
      </Container>
    </section>
  );
}

// ─── Metrics Strip ────────────────────────────────────────────────────────────
function MetricsStrip() {
  const { t } = useTranslation();
  const metrics = ["m1", "m2", "m3"];
  return (
    <section className="py-4 border-bottom" style={{ backgroundColor: "#ffffff" }}>
      <Container>
        <Row className="text-center gy-3 justify-content-center">
          {metrics.map((k) => (
            <Col xs={6} md={4} key={k}>
              <div className="fw-bold fs-2 lh-1" style={{ color: "#0f3460" }}>
                {t(`photographer_landing.metrics.${k}_number`)}
              </div>
              <div className="text-secondary small mt-1">
                {t(`photographer_landing.metrics.${k}_label`)}
              </div>
            </Col>
          ))}
        </Row>
        <p className="text-center mt-3 mb-0" style={{ color: "#aaa", fontSize: "0.75rem" }}>
          {t("photographer_landing.metrics.attribution")}
        </p>
      </Container>
    </section>
  );
}

// ─── Pain Points ──────────────────────────────────────────────────────────────
function PainSection() {
  const { t } = useTranslation();
  const pains = [
    { key: "p1", icon: faFaceSmile, color: "#e63946" },
    { key: "p2", icon: faChartBar, color: "#e63946" },
    { key: "p3", icon: faImages, color: "#e63946" },
  ];
  return (
    <section className="py-5" style={{ backgroundColor: "#f8f9fa" }}>
      <Container>
        <SlideIn>
          <h2 className="text-center fw-bold mb-2">{t("photographer_landing.pain.title")}</h2>
          <p className="text-center text-secondary mb-5">
            {t("photographer_landing.pain.subtitle")}
          </p>
          <Row className="g-4">
            {pains.map(({ key, icon, color }) => (
              <Col md={4} key={key}>
                <Card className="h-100 border-0 shadow-sm rounded-4 p-4">
                  <FontAwesomeIcon icon={icon} size="2x" className="mb-3" style={{ color }} />
                  <h5 className="fw-bold mb-2">{t(`photographer_landing.pain.${key}_title`)}</h5>
                  <p className="text-secondary mb-0">
                    {t(`photographer_landing.pain.${key}_desc`)}
                  </p>
                </Card>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-5">
            <p className="fw-semibold fs-5 mb-1" style={{ color: "#1a1a2e" }}>
              {t("photographer_landing.pain.transition_title")}
            </p>
            <p className="text-secondary mb-0">
              {t("photographer_landing.pain.transition_desc")}
            </p>
          </div>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── Feature Cards ─────────────────────────────────────────────────────────────
function FeatureCardsSection() {
  const { t } = useTranslation();
  const features = [
    { key: "face", icon: faFaceSmile },
    { key: "event_wall", icon: faUsers },
    { key: "social", icon: faShareNodes },
    { key: "albums", icon: faImages },
    { key: "analytics", icon: faChartBar },
    { key: "branding", icon: faPalette },
  ];
  return (
    <section className="py-5" style={{ backgroundColor: "#ffffff" }}>
      <Container>
        <SlideIn>
          <p
            className="text-center text-secondary text-uppercase fw-semibold small mb-5"
            style={{ letterSpacing: "0.1em" }}
          >
            {t("photographer_landing.features.section_label")}
          </p>
          <Row className="g-4">
            {features.map(({ key, icon }) => (
              <Col md={4} key={key}>
                <Card className="h-100 border-0 shadow-sm rounded-4 p-4">
                  <div
                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: 56, height: 56, backgroundColor: "#0f3460" }}
                  >
                    <FontAwesomeIcon icon={icon} className="text-white" />
                  </div>
                  <h5 className="fw-bold mb-2">
                    {t(`photographer_landing.features.${key}_title`)}
                  </h5>
                  <p className="text-secondary mb-0 small">
                    {t(`photographer_landing.features.${key}_desc`)}
                  </p>
                </Card>
              </Col>
            ))}
          </Row>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── Event Wall Highlight ──────────────────────────────────────────────────────
function EventWallHighlight() {
  const { t } = useTranslation();
  const stats = [
    { icon: faUpload, key: "stat1" },
    { icon: faHeart, key: "stat2" },
    { icon: faShareAlt, key: "stat3" },
  ];
  return (
    <div
      className="py-5"
      style={{
        background: "linear-gradient(135deg, #1a1a2e 0%, #4a1942 50%, #7b2d8b 100%)",
        color: "white",
      }}
    >
      <Container>
        <SlideIn>
          <Row className="align-items-center g-5">
            <Col md={6}>
              <span
                className="badge rounded-pill mb-3 d-inline-block px-3 py-2"
                style={{
                  backgroundColor: "#ffd700",
                  color: "#1a1a2e",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: "0.1em",
                }}
              >
                <FontAwesomeIcon icon={faStar} className="me-1" />
                {t("photographer_landing.features.guest_gallery_badge")}
              </span>
              <h2 className="fw-bold mb-3" style={{ fontSize: "2rem" }}>
                {t("photographer_landing.features.guest_gallery_title")}
              </h2>
              <p className="fs-5 mb-4" style={{ opacity: 0.9 }}>
                {t("photographer_landing.features.guest_gallery_desc")}
              </p>
              {["tag1", "tag2", "tag3"].map((tag) => (
                <div className="d-flex align-items-center gap-2 mb-2" key={tag}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#ffd700" }} />
                  <span>{t(`photographer_landing.features.guest_gallery_${tag}`)}</span>
                </div>
              ))}
              <div
                className="my-4 rounded-3 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: "rgba(255,255,255,0.08)", height: 180 }}
              >
                <p className="text-white-50 small mb-0">Event Wall preview coming soon</p>
              </div>
              <Button
                as={Link}
                to="/signup"
                size="lg"
                className="mt-4"
                style={{
                  backgroundColor: "#ffd700",
                  color: "#1a1a2e",
                  border: "none",
                  fontWeight: 700,
                }}
              >
                {t("photographer_landing.features.guest_gallery_cta")}
                <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
              </Button>
            </Col>
            <Col md={6}>
              <div className="d-flex flex-column gap-3">
                {stats.map(({ icon, key }) => (
                  <div
                    key={key}
                    className="d-flex align-items-start gap-3 p-3 rounded-3"
                    style={{ backgroundColor: "rgba(255,255,255,0.08)" }}
                  >
                    <div
                      className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 48,
                        height: 48,
                        backgroundColor: "rgba(255,215,0,0.15)",
                      }}
                    >
                      <FontAwesomeIcon icon={icon} style={{ color: "#ffd700" }} />
                    </div>
                    <div>
                      <div className="fw-bold">
                        {t(`photographer_landing.features.guest_gallery_${key}_title`)}
                      </div>
                      <div className="small" style={{ opacity: 0.75 }}>
                        {t(`photographer_landing.features.guest_gallery_${key}_desc`)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </SlideIn>
      </Container>
    </div>
  );
}

// ─── How It Works ─────────────────────────────────────────────────────────────
function HowItWorksSection() {
  const { t } = useTranslation();
  const steps = [
    { key: "step1", icon: faImages },
    { key: "step2", icon: faQrcode },
    { key: "step3", icon: faFaceSmile },
    { key: "step4", icon: faUsers },
  ];
  return (
    <section className="py-5" style={{ backgroundColor: "#f8f9fa" }}>
      <Container>
        <SlideIn>
          <h2 className="text-center fw-bold mb-2">
            {t("photographer_landing.how_it_works.title")}
          </h2>
          <p className="text-center text-secondary mb-5">
            {t("photographer_landing.how_it_works.subtitle")}
          </p>
          <Row className="g-4">
            {steps.map(({ key, icon }) => (
              <Col xs={6} md={3} key={key}>
                <div className="text-center p-3">
                  <div
                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: 64, height: 64, backgroundColor: "#0f3460" }}
                  >
                    <FontAwesomeIcon icon={icon} size="lg" className="text-white" />
                  </div>
                  <div
                    className="fw-bold mb-1"
                    style={{
                      color: "#0f3460",
                      fontSize: "0.75rem",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    {t(`photographer_landing.how_it_works.${key}_num`)}
                  </div>
                  <h5 className="fw-bold mb-2">
                    {t(`photographer_landing.how_it_works.${key}_title`)}
                  </h5>
                  <p className="text-secondary small mb-0">
                    {t(`photographer_landing.how_it_works.${key}_desc`)}
                  </p>
                </div>
              </Col>
            ))}
          </Row>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────
function TestimonialsSection() {
  const { t } = useTranslation();
  const testimonials = [
    { key: "t1", avatar: "https://i.pravatar.cc/80?img=47" },
    { key: "t2", avatar: "https://i.pravatar.cc/80?img=12" },
    { key: "t3", avatar: "https://i.pravatar.cc/80?img=32" },
  ];
  return (
    <section className="py-5" style={{ backgroundColor: "#ffffff" }}>
      <Container>
        <SlideIn>
          <h2 className="text-center fw-bold mb-2">
            {t("photographer_landing.testimonials.title")}
          </h2>
          <p className="text-center text-secondary mb-5">
            {t("photographer_landing.testimonials.subtitle")}
          </p>
          <Row className="g-4">
            {testimonials.map(({ key, avatar }) => (
              <Col md={4} key={key}>
                <Card className="h-100 border-0 shadow-sm rounded-4 p-4">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <img
                      src={avatar}
                      alt={t(`photographer_landing.testimonials.${key}_name`)}
                      className="rounded-circle"
                      style={{ width: 48, height: 48, objectFit: "cover" }}
                    />
                    <div>
                      <div className="fw-bold">
                        {t(`photographer_landing.testimonials.${key}_name`)}
                      </div>
                      <div className="text-secondary small">
                        {t(`photographer_landing.testimonials.${key}_role`)}
                      </div>
                    </div>
                  </div>
                  <div className="mb-2 d-flex gap-1" style={{ color: "#ffd700" }}>
                    {[...Array(5)].map((_, i) => <FontAwesomeIcon key={i} icon={faStar} />)}
                  </div>
                  <p className="text-secondary mb-0 fst-italic">
                    "{t(`photographer_landing.testimonials.${key}_text`)}"
                  </p>
                </Card>
              </Col>
            ))}
          </Row>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── CTA ──────────────────────────────────────────────────────────────────────
function CTASection() {
  const { t } = useTranslation();
  return (
    <section
      className="py-5 text-center text-white"
      style={{ backgroundColor: "#1a1a2e" }}
    >
      <Container>
        <SlideIn>
          <FontAwesomeIcon
            icon={faBolt}
            size="2x"
            className="mb-4"
            style={{ color: "#ffd700" }}
          />
          <h2 className="fw-bold mb-3">{t("photographer_landing.cta.title")}</h2>
          <p className="fs-5 mb-4" style={{ opacity: 0.75 }}>
            {t("photographer_landing.cta.desc")}
          </p>
          <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
            <Button as={Link} to="/signup" size="lg" className="landing-page-btn px-5">
              {t("photographer_landing.cta.btn")}
            </Button>
            <Button
              as={Link}
              to="/plans"
              size="lg"
              variant="outline-light"
              className="px-5"
            >
              {t("photographer_landing.cta.secondary")}
            </Button>
          </div>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────
function FAQSection() {
  const { t } = useTranslation();
  const faqs = ["q1", "q2", "q3", "q4", "q5", "q6", "q7", "q8"];
  return (
    <section className="py-5" style={{ backgroundColor: "#f8f9fa" }}>
      <Container>
        <SlideIn>
          <h2 className="text-center fw-bold mb-5">
            {t("photographer_landing.faq.title")}
          </h2>
          <Row className="justify-content-center">
            <Col md={8}>
              <Accordion flush defaultActiveKey="4">
                {faqs.map((q, idx) => (
                  <Accordion.Item
                    eventKey={String(idx)}
                    key={q}
                    className="mb-2 rounded-3 border shadow-sm overflow-hidden"
                  >
                    <Accordion.Header>
                      {t(`photographer_landing.faq.${q}`)}
                    </Accordion.Header>
                    <Accordion.Body className="text-secondary">
                      {t(`photographer_landing.faq.a${idx + 1}`)}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </SlideIn>
      </Container>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function PageFooter() {
  const { t } = useTranslation();
  return (
    <footer className="py-4 bg-dark text-white">
      <Container>
        <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-2">
          <a
            href="https://www.pic-loud.com"
            className="d-flex align-items-center text-decoration-none"
          >
            <img
              src="/logo_horizontal_white.png"
              alt="Pic-Loud"
              style={{ width: "auto", height: 20 }}
            />
          </a>
          <nav className="d-flex align-items-center gap-3">
            <Link
              to="/privacy"
              className="text-white-50 text-decoration-none small fw-medium"
            >
              {t("photographer_landing.footer.privacy")}
            </Link>
            <span className="text-white-50 small">|</span>
            <Link
              to="/terms"
              className="text-white-50 text-decoration-none small fw-medium"
            >
              {t("photographer_landing.footer.terms")}
            </Link>
            <span className="text-white-50 small">|</span>
            <Link
              to="/landing-gu"
              className="text-white-50 text-decoration-none small fw-medium"
            >
              {t("photographer_landing.footer.for_guests")}
            </Link>
          </nav>
          <p className="text-white-50 small mb-0">
            &copy; {new Date().getFullYear()} Pic-Loud.{" "}
            {t("photographer_landing.footer.rights")}
          </p>
        </div>
      </Container>
    </footer>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
function PhotographerLandingPage() {
  return (
    <div>
      <HeaderLanding headerVariant="" theme="photographer" />
      <HeroSection />
      <MetricsStrip />
      <PainSection />
      <FeatureCardsSection />
      <EventWallHighlight />
      <HowItWorksSection />
      <TestimonialsSection />
      <CTASection />
      <FAQSection />
      <PageFooter />
    </div>
  );
}

export default PhotographerLandingPage;
